import { inject, Injectable } from "@angular/core";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { Store } from "@ngrx/store";
import {
    catchError,
    exhaustMap,
    filter,
    map,
    merge,
    of,
    withLatestFrom,
} from "rxjs";
import { SocketTransportService } from "../../../core/realtime/socket-transport.service";
import { SessionFacade } from "../../authentication";
import { ContactsApiService } from "../data-access/contacts-api.service";
import { Contact, ContactPresence } from "../models/contact.models";
import { ContactsActions } from "./contacts.actions";
import { contactsFeature } from "./contacts.reducer";
import { withSkeletonTiming } from "../../../shared/operators/skeleton-timing.operator";

interface PresencePayload {
    userId?: string;
    username: string;
    name?: string;
    clientId?: string;
    tags?: string[];
    profilePicture?: string | null;
    mobileUser?: boolean;
    status?: string;
}
@Injectable()
export class ContactsEffects {
    private readonly actions$ = inject(Actions);
    private readonly store = inject(Store);
    private readonly api = inject(ContactsApiService);
    private readonly socket = inject(SocketTransportService);
    private readonly session = inject(SessionFacade);
    readonly load$ = createEffect(() =>
        this.actions$.pipe(
            ofType(
                ContactsActions.pageOpened,
                ContactsActions.loadMoreRequested
            ),
            withLatestFrom(
                this.store.select(contactsFeature.selectContactsState)
            ),
            filter(([action, state]) =>
                action.type === ContactsActions.pageOpened.type
                    ? !state.loaded
                    : state.onlineCursor !== null ||
                      state.sleepingCursor !== null ||
                      state.directoryCursor !== null
            ),
            exhaustMap(([action, state]) => {
                const initial = action.type === ContactsActions.pageOpened.type;
                const request = this.api.load(
                    initial ? undefined : state.onlineCursor,
                    initial ? undefined : state.sleepingCursor,
                    initial ? undefined : state.directoryCursor
                );
                return (
                    initial ? request.pipe(withSkeletonTiming()) : request
                ).pipe(
                    map((page) =>
                        ContactsActions.pageLoaded({ ...page, initial })
                    ),
                    catchError(() => of(ContactsActions.pageLoadFailed()))
                );
            })
        )
    );
    readonly presence$ = createEffect(() =>
        merge(
            this.socket.on<PresencePayload>("user_added").pipe(
                map((payload) => ({
                    payload,
                    presence: this.mapPresence(payload.status, "online"),
                }))
            ),
            this.socket.on<PresencePayload>("userAdded").pipe(
                map((payload) => ({
                    payload,
                    presence: this.mapPresence(payload.status, "online"),
                }))
            ),
            this.socket
                .on<PresencePayload>("user_calling")
                .pipe(
                    map((payload) => ({ payload, presence: "busy" as const }))
                ),
            this.socket
                .on<PresencePayload>("userCalling")
                .pipe(
                    map((payload) => ({ payload, presence: "busy" as const }))
                ),
            this.socket.on<PresencePayload>("user_removed").pipe(
                map((payload) => ({
                    payload,
                    presence: "offline" as const,
                }))
            ),
            this.socket.on<PresencePayload>("userRemoved").pipe(
                map((payload) => ({
                    payload,
                    presence: "offline" as const,
                }))
            )
        ).pipe(
            filter(
                ({ payload }) =>
                    Boolean(payload?.username) &&
                    payload.username !== this.session.user()?.username
            ),
            map(({ payload, presence }) =>
                ContactsActions.presenceChanged({
                    contact: this.toContact(payload, presence),
                    presence,
                })
            )
        )
    );
    private mapPresence(
        status: string | undefined,
        fallback: ContactPresence
    ): ContactPresence {
        return status === "unavailable"
            ? "busy"
            : status === "offline" || status === "sleeping"
              ? "offline"
              : fallback;
    }
    private toContact(
        payload: PresencePayload,
        presence: ContactPresence
    ): Contact {
        return {
            id: payload.userId || payload.username,
            username: payload.username,
            name: payload.name || payload.username,
            clientId: payload.clientId || "",
            tags: payload.tags ?? [],
            profilePicture: payload.profilePicture ?? null,
            mobileUser: Boolean(payload.mobileUser),
            presence,
        };
    }
}
